import { useState, useEffect, useCallback } from 'react';
import { getJobPaymentSummary } from '../api/jobPayments';
import type { PaymentSummary } from '../types/jobPayment';

/**
 * Hook for fetching job payment summary
 */
export function useJobPaymentSummary(jobId: number | undefined, isForecast?: boolean) {
  const [data, setData] = useState<PaymentSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    if (!jobId) {
      setData(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const summary = await getJobPaymentSummary(jobId, isForecast);
      setData(summary);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch payment summary');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [jobId, isForecast]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchSummary,
  };
}
